import { PersonLabelContext, UserStatus } from '@bp/bp-graphql-types';
import { ProfileRoles } from '@bp/pim-auth-constants';
import {
  AddIcon,
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  Grid,
  GridRow,
} from '@bp/ui-components';
import { LoadingModal } from 'components/LoadingModal/LoadingModal';
import { PersonsLabelFilter } from 'components/Persons/components/PersonsLabelFilter/PersonsLabelFilter';
import { PersonsList } from 'components/Persons/components/PersonsList/PersonsList';
import { CreateUserModal } from 'components/Persons/modals/CreateUserModal';
import { PersonAdminRolesModal } from 'components/Persons/modals/PersonAdminRolesModal';
import { PersonModal } from 'components/Persons/modals/PersonModal';
import { PersonParentModal } from 'components/Persons/modals/PersonParentModal';
import { PersonProfileRoleModal } from 'components/Persons/modals/PersonProfileRoleModal';
import { PersonsBulkInviteModal } from 'components/Persons/modals/PersonsBulkInviteModal';
import { PersonLabelSource, PersonLabelType } from 'components/Persons/types/personTypes';
import { labelIsInLabels } from 'components/Persons/utils/personUtils';
import { Sticky } from 'components/Sticky/Sticky';
import { ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';
import { graphql } from '../../../client/gql';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { usePersonBulkActions } from '../../../components/Persons/hooks/usePersonBulkActions';
import { AppLayoutContext } from '../../../context/AppLayoutContext';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import styles from './InstitutionSubpages.module.scss';

const DeleteProfileMutationDocument = graphql(`
  mutation BpDeleteProfile($uuid: String!) {
    deleteBpProfile(uuid: $uuid) {
      entitiesDeleted
    }
  }
`);

const DisconnectProfileMutationDocument = graphql(`
  mutation BpDisconnectProfile($uuid: String!) {
    disconnectBpProfile(uuid: $uuid) {
      uuid
      __typename
    }
  }
`);

const MergeProfilesMutationDocument = graphql(`
  mutation MergeProfilesMutation($uuids: [ID!]!) {
    mergeBpProfiles(uuids: $uuids) {
      uuid
      __typename
    }
  }
`);

export const InstitutionPersonsSubpageQueryDocument = graphql(`
  query InstitutionPersonsSubpageData(
    $organizationUuid: ID!
    $labelUuids: [ID!]!
    $schoolyearUuid: ID!
    $labelContext: PersonLabelContext!
  ) {
    profiles(where: { organization: { uuid: $organizationUuid } }, options: { sort: [{ listName: ASC }] }) {
      __typename
      uuid
      updatedAt
      firstName
      lastName
      listName
      email
      birthday
      organizationRoles
      user {
        __typename
        uuid
        loginEmail
        status
      }
      threemaConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
          }
          node {
            __typename
            licenseCount
          }
        }
      }
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
          }
        }
      }
    }
    ...PersonsLabelFilterFields
    ...PersonsListFields
    ...PersonsBulkInviteModalFields
  }
`);

type InstiutionPersonType = {
  uuid: string;
  firstName: string | null;
  lastName: string | null;
  listName: string | null;
  organizationRoles: string[] | null;
  userStatus: UserStatus;
  hasThreema: boolean;
  hasNextcloud: boolean;
  hasMailbox: boolean;
};

export const InstitutionPersonsSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { threemaLicense, cloudLicense, mailboxLicense, currentSchoolYear } = useContext(OrganizationConfigContext);
  const { scrollRef } = useContext(AppLayoutContext);

  const [{ data }, refetchProfiles] = useQuery({
    query: InstitutionPersonsSubpageQueryDocument,
    variables: {
      organizationUuid,
      labelUuids: [organizationUuid],
      schoolyearUuid: currentSchoolYear.uuid,
      labelContext: PersonLabelContext.Organization,
    },
  });

  const permissions = usePermissionChecker();

  const persons =
    data?.profiles.map((p) => {
      return {
        uuid: p.uuid,
        updatedAt: p.updatedAt,
        firstName: p.firstName ?? null,
        lastName: p.lastName ?? null,
        listName: p.listName ?? null,
        email: p.user?.loginEmail ?? null,
        birthday: p.birthday ?? null,
        organizationRoles: p.organizationRoles ?? null,
        userStatus: p.user?.status ?? UserStatus.None,
        hasThreema: !!p.threemaConnection?.edges?.[0]?.properties?.enabled,
        hasNextcloud: !!p.nextcloudConnection?.edges?.[0]?.properties?.enabled,
        hasMailbox: false, // TODO: implement
      };
    }) ?? [];

  // filter and search
  const [activeSources, setActiveSources] = useState<PersonLabelSource[]>([
    'AddOn',
    'Class',
    'ProfileRole',
    'UserStatus',
  ]);

  const [filterMode, setFilterMode] = useState<Record<PersonLabelSource, 'and' | 'or'>>({
    AccessRole: 'and',
    AddOn: 'and',
    Child: 'and',
    Class: 'and',
    ClassGroup: 'or',
    GroupRole: 'and',
    Parent: 'and',
    ProfileRole: 'and',
    UserStatus: 'and',
  });

  const [search, setSearch] = useState<string>('');
  const [selectedLabels, setSelectedLabels] = useState<PersonLabelType[]>([]);
  const [currentPersonCount, setCurrentPersonCount] = useState<number>(0);
  const [maxPersonCount, setMaxPersonCount] = useState<number>(0);

  const threemaConnections = data?.profiles.map((p) => p.threemaConnection).filter((tc) => tc.edges.length > 0) ?? [];
  const used = threemaConnections.length;
  const booked =
    used > 0 && threemaConnections[0].edges[0] ? (threemaConnections[0].edges[0].node.licenseCount ?? 0) : 0;
  const freeThreemaLicenseCount = booked - used;

  // bulk actions hook
  const {
    selectedPersonUuids,
    handlePersonSelect,
    handleSelectAll,
    clearSelection,
    handleAddThreema,
    handleDeleteThreema,
    handleBulkNextcloud,
    handleDeleteNextcloud,
    ConfirmationDialogs: BulkActionsConfirmationDialogs,
  } = usePersonBulkActions({
    freeThreemaLicenseCount,
    onSuccess: () => refetchProfiles({ requestPolicy: 'network-only' }),
  });

  // menues and functionality
  const [personData, setPersonData] = useState<{
    uuid?: string;
    name?: string;
    role?: ProfileRoles;
    status?: UserStatus;
  } | null>(null);
  const [personModalOpen, setPersonModalOpen] = useState<boolean>(false);
  const [profileRoleModalOpen, setProfileRoleModalOpen] = useState<boolean>(false);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState<boolean>(false);
  const [parentModalOpen, setParentModalOpen] = useState<boolean>(false);
  const [adminRolesModalOpen, setAdminRolesModalOpen] = useState<boolean>(false);
  const [bulkInviteModalOpen, setBulkInviteModalOpen] = useState<boolean>(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>();

  const [, deleteProfile] = useMutation(DeleteProfileMutationDocument);
  const [, disconnectProfile] = useMutation(DisconnectProfileMutationDocument);
  const [, mergeProfiles] = useMutation(MergeProfilesMutationDocument);

  // confirms
  const { ConfirmationDialog: ProfileConfirmationDialog, confirm: confirmProfileDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('profiles.titleSingular'),
      context: 'neutrum',
    }),
    defaultConfirmText: t('delete.delete'),
  });
  const { ConfirmationDialog: ProfileDisconnectConfirmationDialog, confirm: confirmProfileDisconnect } = useConfirm({
    defaultTitle: t('profiles.deleteAccount'),
    defaultMessage: t('delete.message', {
      type: t('common.account'),
      context: 'male',
    }),
    defaultConfirmText: t('profiles.deleteAccount'),
  });

  const { ConfirmationDialog: MergeConfirmationDialog, confirm: confirmMerge } = useConfirm({
    defaultTitle: t('profiles.mergeProfiles'),
    defaultConfirmText: t('common.confirm'),
  });

  const emptySelection = selectedPersonUuids.length === 0;

  const bulkEditContent: DropdownMenuItem[] = [
    {
      label: t('externalServices.createCloudAccount'),
      onClick: () => handleBulkNextcloud(selectedPersonUuids),
      disabled: emptySelection,
    },
    {
      label: t('profiles.invite.sendInvitation'),
      onClick: () => handleBulkInvite(),
      disabled: emptySelection,
    },
    {
      label: t('profiles.mergeProfiles'),
      onClick: () => handleMergeProfiles(),
      disabled: emptySelection,
    },
    {
      label: t('externalServices.assignThreemaLicense'),
      onClick: () => handleAddThreema(selectedPersonUuids),
      disabled: emptySelection,
    },
  ];

  const addButtonContent = (
    <Dropdown
      usePortal={false}
      trigger={
        <Button hierarchy='tertiary' icon={<AddIcon />}>
          {t('common.addType', { type: t('persons.title', { count: 1 }) })}
        </Button>
      }
    >
      <DropdownMenu
        data={[
          {
            label: t('common.addType', {
              type: t('rolesInOrganization.teacher', { count: 1 }),
            }),
            onClick: () => handlePersonCreate(ProfileRoles.Teacher),
          },
          {
            label: t('common.addType', {
              type: t('rolesInOrganization.student', { count: 1 }),
            }),
            onClick: () => handlePersonCreate(ProfileRoles.Student),
          },
          {
            label: t('common.addType', {
              type: t('rolesInOrganization.parent', { count: 1 }),
            }),
            onClick: () => handlePersonCreate(ProfileRoles.Parent),
          },
          {
            label: t('common.addType', {
              type: `${t('rolesInOrganization.other', { count: 1 })} (${t('rolesInOrganization.other', { context: 'short' })})`,
            }),
            onClick: () => handlePersonCreate(ProfileRoles.Other),
          },
        ]}
      />
    </Dropdown>
  );

  function getActions(personUuid: string): ReactNode {
    const person = persons.find((p) => p.uuid === personUuid);
    if (!person) return [];

    const items: DropdownMenuItem[] = [];

    const { uuid, userStatus, organizationRoles, hasThreema, hasNextcloud, hasMailbox } = person;

    const profileSubject = {
      uuid: uuid,
      organization: { uuid: organizationUuid },
    };

    if (permissions?.canUpdateProfile(profileSubject))
      items.push({
        label: t('profiles.editProfile'),
        onClick: (e) => {
          e.stopPropagation();
          handlePersonEdit(person);
        },
      });

    if (permissions?.canUpdateProfile(profileSubject))
      items.push({
        label: t('profiles.changeRole'),
        onClick: (e) => {
          e.stopPropagation();
          handleRoleChange(person);
        },
      });

    if (
      permissions?.canUpdateProfile(profileSubject) &&
      (userStatus === UserStatus.None || userStatus === UserStatus.ImpersonateOnly)
    )
      items.push({
        label: t('profiles.createAccount'),
        onClick: (e) => {
          e.stopPropagation();
          handleProfileConnect(person);
        },
      });

    if (permissions?.canUpdateProfile(profileSubject) && userStatus === UserStatus.Full)
      items.push({
        label: t('profiles.disconnect'),
        onClick: (e) => {
          e.stopPropagation();
          handleDisconnectProfiles([person]);
        },
      });

    items.push({ type: 'ruler' });

    if (permissions?.canUpdateProfile(profileSubject) && userStatus === UserStatus.Invited)
      items.push({
        label: t('profiles.invite.resendInvitation'),
        onClick: async (e) => {
          e.stopPropagation();
          handleProfileConnect(person);
        },
      });

    items.push({ type: 'ruler' });

    if (organizationRoles?.includes(ProfileRoles.Student)) {
      items.push({
        label: t('profiles.changeParents'),
        onClick: (e) => {
          e.stopPropagation();
          handleModifyParents(person);
        },
      });
      items.push({ type: 'ruler' });
    }

    if (permissions?.isOmniAdmin() && userStatus !== UserStatus.None) {
      items.push({
        label: t('profiles.grantRoles'),
        onClick: (e) => {
          e.stopPropagation();
          handleChangeAdminRoles(person);
        },
      });
      items.push({ type: 'ruler' });
    }

    if (!hasThreema)
      items.push({
        label: t('externalServices.assignThreemaLicense'),
        disabled: !threemaLicense || !person.email,
        onClick: (e) => {
          e.stopPropagation();
          void handleAddThreema([person.uuid]);
        },
      });

    if (threemaLicense && hasThreema)
      items.push({
        label: t('externalServices.unassignThreemaLicense'),
        onClick: (e) => {
          e.stopPropagation();
          void handleDeleteThreema([person.uuid]);
        },
      });

    if (!hasMailbox)
      items.push({
        label: t('externalServices.createMailboxAccount'),
        disabled: !mailboxLicense,
        onClick: (e) => {
          e.stopPropagation();
          handleAddMailbox([person]);
        },
      });

    if (mailboxLicense && hasMailbox)
      items.push({
        label: t('externalServices.deleteMailboxAccount'),
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteMailbox([person]);
        },
      });

    if (!hasNextcloud)
      items.push({
        label: t('externalServices.createCloudAccount'),
        disabled: !cloudLicense,
        onClick: (e) => {
          e.stopPropagation();
          void handleAddThreema([person.uuid]);
        },
      });

    if (cloudLicense && hasNextcloud)
      items.push({
        label: t('externalServices.deleteCloudAccount'),
        onClick: (e) => {
          e.stopPropagation();
          void handleDeleteNextcloud([person.uuid]);
        },
      });

    if (permissions?.canDeleteProfile(profileSubject)) {
      items.push({ type: 'ruler' });
      items.push({
        label: t('profiles.deleteProfile'),
        color: 'error',
        disabled: person.userStatus !== UserStatus.None,
        onClick: async (e) => {
          e.stopPropagation();
          await handlePersonDelete(person);
        },
      });
    }

    if (permissions?.canDeleteProfile(profileSubject)) {
      items.push({
        label: t('profiles.deleteAccount'),
        color: 'error',
        onClick: async (e) => {
          e.stopPropagation();
          await handlePersonDisconnect(person);
        },
      });
    }

    return (
      <Dropdown trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon />} />}>
        <DropdownMenu data={items} />
      </Dropdown>
    );
  }

  function findProfileRole(roles: string[] | null): ProfileRoles {
    const allowedRoles = [ProfileRoles.Teacher, ProfileRoles.Student, ProfileRoles.Parent, ProfileRoles.Other];
    if (!roles || roles.length === 0) {
      return ProfileRoles.Student;
    } else {
      return allowedRoles.find((r) => roles.includes(r)) ?? ProfileRoles.Student;
    }
  }

  function handlePersonCreate(role: ProfileRoles) {
    setPersonData({ role });
    setPersonModalOpen(true);
  }

  function handlePersonEdit(person: InstiutionPersonType) {
    setPersonData({ uuid: person.uuid, role: findProfileRole(person.organizationRoles) });
    setPersonModalOpen(true);
  }

  function handleRoleChange(person: InstiutionPersonType) {
    setPersonData({ uuid: person.uuid, name: `${person.firstName} ${person.lastName}` });
    setProfileRoleModalOpen(true);
  }

  function handleProfileConnect(person: InstiutionPersonType) {
    setPersonData({ uuid: person.uuid, status: person.userStatus });
    setInviteUserModalOpen(true);
  }

  function handleDisconnectProfiles(_persons: InstiutionPersonType[]) {
    // TODO: implement
  }

  function handleModifyParents(person: InstiutionPersonType) {
    setPersonData({ uuid: person.uuid, name: `${person.firstName} ${person.lastName}` });
    setParentModalOpen(true);
  }

  function handleChangeAdminRoles(person: InstiutionPersonType) {
    setPersonData({ uuid: person.uuid, name: `${person.firstName} ${person.lastName}` });
    setAdminRolesModalOpen(true);
  }

  async function handleAddMailbox(_persons: InstiutionPersonType[]) {
    // TODO: implement
  }

  async function handleDeleteMailbox(_persons: InstiutionPersonType[]) {
    // TODO: implement
  }

  async function handlePersonDelete(person: InstiutionPersonType) {
    const confirm = await confirmProfileDelete();
    if (!confirm) return;
    const { error } = await deleteProfile({ uuid: person.uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
      refetchProfiles({ requestPolicy: 'network-only' });
    }
  }

  async function handlePersonDisconnect(person: InstiutionPersonType) {
    const confirm = await confirmProfileDisconnect();
    if (!confirm) return;
    const { error } = await disconnectProfile({ uuid: person.uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
      refetchProfiles({ requestPolicy: 'network-only' });
    }
  }

  function handleBulkInvite() {
    setBulkInviteModalOpen(true);
  }

  async function handleMergeProfiles() {
    const profiles = data?.profiles.filter((p) => selectedPersonUuids.includes(p.uuid)) ?? [];
    const users = profiles.filter((p) => p.user?.uuid) ?? [];
    if (users.length > 1) {
      showErrorToast({
        message: t('profiles.multipleUsersError'),
        name: '',
        graphQLErrors: [],
      });
      return;
    }
    const confirm = await confirmMerge({
      message: t('profiles.confirm.mergeProfiles', {
        profiles: profiles.map((u) => `${u.firstName} ${u.lastName}`).join('\n'),
      }),
    });
    if (!confirm || selectedPersonUuids.length === 0) return;
    setLoadingMessage(t('profiles.mergeInProgress'));
    setLoadingModalOpen(true);
    const { error } = await mergeProfiles({ uuids: selectedPersonUuids });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast(t('toast.success.merge', { count: selectedPersonUuids.length }));
    }
    reset(true);
  }

  function handleLabelToggle(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (!labelIsInLabels(label, prev)) {
        return [...prev, label];
      } else {
        return prev.filter((l) => !labelIsInLabels(label, [l]));
      }
    });
  }

  function handleLabelAdd(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (!labelIsInLabels(label, prev)) {
        return [...prev, label];
      }
      return prev;
    });
  }

  function handleLabelClose(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (labelIsInLabels(label, prev)) {
        return prev.filter((l) => l !== label);
      }
      return prev;
    });
  }

  function handleCloseAllLabels() {
    setSelectedLabels([]);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  function handleSourceFilter(sources: PersonLabelSource[]) {
    setActiveSources(sources);
  }

  function handleFilterModeChange(source: PersonLabelSource, mode: 'and' | 'or') {
    setFilterMode((prev) => ({
      ...prev,
      [source]: mode,
    }));
  }

  function handlePersonCountChanged(current: number, max: number) {
    setCurrentPersonCount(current);
    setMaxPersonCount(max);
  }

  function reset(refetch?: boolean) {
    setPersonData(null);
    setPersonModalOpen(false);
    setProfileRoleModalOpen(false);
    setInviteUserModalOpen(false);
    setParentModalOpen(false);
    setAdminRolesModalOpen(false);
    setBulkInviteModalOpen(false);
    setLoadingModalOpen(false);
    setLoadingMessage(null);
    clearSelection();
    if (refetch) refetchProfiles();
  }

  const isDesktop = useIsDesktop();

  return (
    <BpSubpage className={styles.persons} isForbidden={!permissions?.canCreateProfile({ uuid: organizationUuid })}>
      <Grid>
        <Sticky top={isDesktop ? 60 : 0} stickyClassName={styles.filterSticky}>
          <GridRow spacingBottom={'m'} spacingTop={'m'}>
            <PersonsLabelFilter
              selectedLabels={selectedLabels}
              showSourcesFilter
              availableSources={[
                'AccessRole',
                'AddOn',
                'Child',
                'Class',
                'ClassGroup',
                'Parent',
                'ProfileRole',
                'UserStatus',
              ]}
              activeSources={activeSources}
              filterMode={filterMode}
              showQuickSelection
              showSearch
              showCount
              currentCount={currentPersonCount}
              maxCount={maxPersonCount}
              showBulkEdit
              bulkEditContent={bulkEditContent}
              bulkEditDisabled={emptySelection}
              showAddButton
              addButtonContent={addButtonContent}
              onQuickSelection={(label) => handleLabelToggle(label)}
              onLabelClose={(label) => handleLabelClose(label)}
              onCloseAll={() => handleCloseAllLabels()}
              onSearch={(value) => handleSearch(value)}
              onSourceFilter={(sources) => handleSourceFilter(sources)}
              data={data}
              onFilterModeChange={handleFilterModeChange}
            />
          </GridRow>
        </Sticky>
        <GridRow style={{ marginTop: 0 }}>
          <BpCard noPadding>
            <PersonsList
              persons={persons}
              selectedLabels={selectedLabels}
              filterMode={filterMode}
              search={search}
              activeSources={activeSources}
              selectedPersons={selectedPersonUuids}
              showBirthdays
              emptyStatePadding='xl'
              onGetActions={(uuid) => getActions(uuid)}
              onSelectCheckbox={(uuid) => handlePersonSelect(uuid)}
              onSelectAll={(uuids) => handleSelectAll(uuids)}
              onLabelClick={(label) => handleLabelAdd(label)}
              onCountChanged={(current, max) => handlePersonCountChanged(current, max)}
              data={data}
              scrollContainer={scrollRef}
            />
          </BpCard>
        </GridRow>
      </Grid>

      {personData && personData.role && (
        <PersonModal
          uuid={personData.uuid}
          role={personData.role}
          isOpen={personModalOpen}
          onClose={() => reset(true)}
        />
      )}

      {personData && personData.uuid && personData.status && (
        <CreateUserModal
          isOpen={inviteUserModalOpen}
          profileUuid={personData.uuid}
          resend={personData.status === UserStatus.Invited}
          onClose={() => reset(true)}
        />
      )}

      {personData && personData.uuid && personData.name && (
        <PersonProfileRoleModal
          personUuid={personData.uuid}
          personName={personData.name}
          isOpen={profileRoleModalOpen}
          onClose={() => reset(true)}
        />
      )}

      {personData && personData.uuid && personData.name && (
        <PersonParentModal
          childUuid={personData.uuid}
          childName={personData.name}
          isOpen={parentModalOpen}
          onClose={() => reset(true)}
        />
      )}

      {personData && personData.uuid && personData.name && (
        <PersonAdminRolesModal
          personUuid={personData.uuid}
          personName={personData.name}
          isOpen={adminRolesModalOpen}
          onClose={() => reset()}
        />
      )}

      <PersonsBulkInviteModal
        isOpen={bulkInviteModalOpen}
        selectedUuids={selectedPersonUuids}
        onClose={() => reset(true)}
        data={data}
      />

      <LoadingModal
        isOpen={loadingModalOpen}
        message={loadingMessage ?? undefined}
        onClose={() => {
          setLoadingModalOpen(false);
          setLoadingMessage(null);
        }}
      />

      <ProfileConfirmationDialog />
      <ProfileDisconnectConfirmationDialog />
      <MergeConfirmationDialog />
      <BulkActionsConfirmationDialogs />
    </BpSubpage>
  );
};
